import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../../../services/api';
import {
  FiSave, FiEye, FiLock, FiSettings, FiBell, FiShield,
  FiBox, FiPlus, FiEdit2, FiEdit, FiTrash2, FiMapPin, FiX, FiAlertTriangle, FiUsers, FiTruck
} from 'react-icons/fi';
import SettingsBlock from '../../../components/SettingsBlock';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import PermissionButton from '../../../components/PermissionButton';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { PermissionGate, usePermissions } from '../../../contexts/PermissionContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { 
  validatePhoneForCountry, 
  formatPhoneForCountry, 
  getPhoneFormatExample,
  COUNTRIES,
  getStatesProvinces,
  getStateProvinceLabel
} from '../../../utils/validation';

const toDateInputValue = (value) => {
  if (!value) return new Date().toISOString().slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return new Date().toISOString().slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const getBlankChangelogForm = () => ({
  version: process.env.REACT_APP_APP_VERSION || '',
  title: '',
  summary: '',
  body_markdown: '',
  admin_only: false,
  is_published: true,
  published_at: new Date().toISOString().slice(0, 10)
});

const ChangelogSettingsForm = ({ canEdit }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState(getBlankChangelogForm);

  const { data, isLoading, error } = useQuery(
    'changelog-settings',
    () => adminAPI.getChangelogEntriesForSettings(),
    { enabled: canEdit }
  );

  const entries = data?.entries || [];

  const resetForm = () => {
    setEditingEntry(null);
    setFormData(getBlankChangelogForm());
  };

  const createEntry = useMutation(
    (payload) => adminAPI.createChangelogEntry(payload),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('changelog-settings');
        queryClient.invalidateQueries('changelog');
        resetForm();
        showSuccess(response?.message || 'Changelog entry created');
      },
      onError: (mutationError) => {
        showError(mutationError.response?.data?.error || 'Failed to create changelog entry');
      }
    }
  );

  const updateEntry = useMutation(
    ({ id, payload }) => adminAPI.updateChangelogEntry(id, payload),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('changelog-settings');
        queryClient.invalidateQueries('changelog');
        resetForm();
        showSuccess(response?.message || 'Changelog entry updated');
      },
      onError: (mutationError) => {
        showError(mutationError.response?.data?.error || 'Failed to update changelog entry');
      }
    }
  );

  const deleteEntry = useMutation(
    (id) => adminAPI.deleteChangelogEntry(id),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('changelog-settings');
        queryClient.invalidateQueries('changelog');
        resetForm();
        showSuccess(response?.message || 'Changelog entry deleted');
      },
      onError: (mutationError) => {
        showError(mutationError.response?.data?.error || 'Failed to delete changelog entry');
      }
    }
  );

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      version: entry.version || '',
      title: entry.title || '',
      summary: entry.summary || '',
      body_markdown: entry.body_markdown || '',
      admin_only: !!entry.admin_only,
      is_published: !!entry.is_published,
      published_at: toDateInputValue(entry.published_at)
    });
  };

  const handleChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = {
      ...formData,
      version: formData.version.trim(),
      title: formData.title.trim(),
      summary: formData.summary.trim(),
      body_markdown: formData.body_markdown.trim()
    };

    if (!payload.version || !payload.title || !payload.body_markdown) {
      showError('Version, title, and changelog body are required');
      return;
    }

    if (editingEntry) {
      updateEntry.mutate({ id: editingEntry.id, payload });
    } else {
      createEntry.mutate(payload);
    }
  };

  if (!canEdit) {
    return (
      <p className="text-sm text-gray-500">
        You need edit general settings permission to manage changelog entries.
      </p>
    );
  }

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="Failed to load changelog entries" />;

  const isSaving = createEntry.isLoading || updateEntry.isLoading;

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="rounded-lg border border-gray-200 bg-gray-50 p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {editingEntry ? 'Edit Changelog Entry' : 'New Changelog Entry'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Markdown is stored in Postgres and rendered on the customer/admin changelog pages.
            </p>
          </div>
          {editingEntry && (
            <button
              type="button"
              onClick={resetForm}
              className="text-sm font-medium text-gray-500 hover:text-gray-900"
            >
              Cancel edit
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Version *</label>
            <input
              type="text"
              value={formData.version}
              onChange={(event) => handleChange('version', event.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="1.4.0"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Publish Date *</label>
            <input
              type="date"
              value={formData.published_at}
              onChange={(event) => handleChange('published_at', event.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>
          <div className="flex items-end gap-5">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={formData.is_published}
                onChange={(event) => handleChange('is_published', event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Published
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={formData.admin_only}
                onChange={(event) => handleChange('admin_only', event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Admin only
            </label>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(event) => handleChange('title', event.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="Inventory receiving improvements"
            required
          />
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">Summary</label>
          <input
            type="text"
            value={formData.summary}
            onChange={(event) => handleChange('summary', event.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="Short one-line summary for the changelog table"
          />
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">Body Markdown *</label>
          <textarea
            value={formData.body_markdown}
            onChange={(event) => handleChange('body_markdown', event.target.value)}
            rows={7}
            className="mt-1 block w-full rounded-md border-gray-300 font-mono text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="- Added customer inventory pagination&#10;- Improved ASN pallet display"
            required
          />
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            <FiSave className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : editingEntry ? 'Update Entry' : 'Create Entry'}
          </button>
        </div>
      </form>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Version</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Audience</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {entries.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-500">
                  No changelog entries yet.
                </td>
              </tr>
            ) : entries.map((entry) => (
              <tr key={entry.id}>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-gray-900">{entry.version}</td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="font-medium">{entry.title}</div>
                  <div className="text-xs text-gray-500">{toDateInputValue(entry.published_at)}</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{entry.admin_only ? 'Admin only' : 'All users'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{entry.is_published ? 'Published' : 'Draft'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                  <button
                    type="button"
                    onClick={() => handleEdit(entry)}
                    className="mr-4 text-indigo-600 hover:text-indigo-900"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Delete changelog entry "${entry.title}"?`)) {
                        deleteEntry.mutate(entry.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// General Settings Form Component
const GeneralSettingsForm = ({ settings, warehouses, timezones, canEdit, onSave, isSaving }) => {
  const defaultNotificationPreferences = {
    request_created: true,
    invoice_created: true,
    manifest_updates: true,
    crossdock_updates: true,
    low_stock: true,
    receiving_updates: true,
    shipment_updates: true
  };
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state_province: '',
    billing_postal_code: '',
    billing_country: 'CA',
    billing_email: '',
    billing_phone: '',
    default_timezone: 'America/Edmonton',
    default_date_format: 'MM/DD/YYYY',
    default_time_format: '24h',
    default_internal_wh: '',
    contact_name: '',
    contact_email: '',
    support_email: '',
    support_notification_emails_text: '',
    notification_preferences: defaultNotificationPreferences,
    contact_phone: '',
    logo_url: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Update form data when settings change
  useEffect(() => {
    if (settings) {
      setFormData({
        company_name: settings.company_name || '',
        billing_address_line1: settings.billing_address_line1 || '',
        billing_address_line2: settings.billing_address_line2 || '',
        billing_city: settings.billing_city || '',
        billing_state_province: settings.billing_state_province || '',
        billing_postal_code: settings.billing_postal_code || '',
        billing_country: settings.billing_country || 'CA', // Default to Canada
        billing_email: settings.billing_email || '',
        billing_phone: settings.billing_phone || '',
        default_timezone: settings.default_timezone || 'America/Edmonton',
        default_date_format: settings.default_date_format || 'MM/DD/YYYY',
        default_time_format: settings.default_time_format || '24h',
        default_internal_wh: settings.default_internal_wh || '',
        contact_name: settings.contact_name || '',
        contact_email: settings.contact_email || '',
        support_email: settings.support_email || '',
        support_notification_emails_text: Array.isArray(settings.support_notification_emails) ? settings.support_notification_emails.join('\n') : '',
        notification_preferences: { ...defaultNotificationPreferences, ...(settings.notification_preferences || {}) },
        contact_phone: settings.contact_phone || '',
        logo_url: settings.logo_url || ''
      });
      
      // Reset logo file state when settings change
      setLogoFile(null);
      setLogoPreview(null);
    }
  }, [settings]);

  // Get available states/provinces based on selected country
  const availableStatesProvinces = getStatesProvinces(formData.billing_country);
  const stateProvinceLabel = getStateProvinceLabel(formData.billing_country);

  const validateForm = () => {
    const errors = {};

    // Required field validation
    if (!formData.company_name) errors.company_name = 'Company name is required';
    if (!formData.billing_address_line1) errors.billing_address_line1 = 'Address line 1 is required';
    if (!formData.billing_city) errors.billing_city = 'City is required';
    if (!formData.billing_state_province) errors.billing_state_province = `${stateProvinceLabel} is required`;
    if (!formData.billing_postal_code) errors.billing_postal_code = 'Postal code is required';
    if (!formData.billing_country) errors.billing_country = 'Country is required';
    if (!formData.billing_email) errors.billing_email = 'Billing email is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.billing_email && !emailRegex.test(formData.billing_email)) {
      errors.billing_email = 'Please enter a valid email address';
    }
    if (formData.contact_email && !emailRegex.test(formData.contact_email)) {
      errors.contact_email = 'Please enter a valid email address';
    }
    if (formData.support_email && !emailRegex.test(formData.support_email)) {
      errors.support_email = 'Please enter a valid email address';
    }
    if (formData.support_notification_emails_text) {
      const emails = formData.support_notification_emails_text
        .split(/[\n,;]/)
        .map((email) => email.trim())
        .filter(Boolean);
      const invalid = emails.find((email) => !emailRegex.test(email));
      if (invalid) {
        errors.support_notification_emails_text = `Invalid email: ${invalid}`;
      }
    }

    // Phone validation based on country
    if (formData.billing_phone && !validatePhoneForCountry(formData.billing_phone, formData.billing_country)) {
      errors.billing_phone = `Phone must be in format: ${getPhoneFormatExample(formData.billing_country)}`;
    }
    if (formData.contact_phone && !validatePhoneForCountry(formData.contact_phone, formData.billing_country)) {
      errors.contact_phone = `Phone must be in format: ${getPhoneFormatExample(formData.billing_country)}`;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    
    // Prepare submission data including logo file
    const submissionData = { ...formData };
    submissionData.support_notification_emails = formData.support_notification_emails_text
      .split(/[\n,;]/)
      .map((email) => email.trim())
      .filter(Boolean);
    delete submissionData.support_notification_emails_text;
    if (logoFile) {
      submissionData.logo_file = logoFile;
    }
    
    onSave(submissionData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Reset form data to original settings
    if (settings) {
      setFormData({
        company_name: settings.company_name || '',
        billing_address_line1: settings.billing_address_line1 || '',
        billing_address_line2: settings.billing_address_line2 || '',
        billing_city: settings.billing_city || '',
        billing_state_province: settings.billing_state_province || '',
        billing_postal_code: settings.billing_postal_code || '',
        billing_country: settings.billing_country || 'CA',
        billing_email: settings.billing_email || '',
        billing_phone: settings.billing_phone || '',
        default_timezone: settings.default_timezone || 'America/Edmonton',
        default_date_format: settings.default_date_format || 'MM/DD/YYYY',
        default_time_format: settings.default_time_format || '24h',
        default_internal_wh: settings.default_internal_wh || '',
        contact_name: settings.contact_name || '',
        contact_email: settings.contact_email || '',
        support_email: settings.support_email || '',
        support_notification_emails_text: Array.isArray(settings.support_notification_emails) ? settings.support_notification_emails.join('\n') : '',
        notification_preferences: { ...defaultNotificationPreferences, ...(settings.notification_preferences || {}) },
        contact_phone: settings.contact_phone || '',
        logo_url: settings.logo_url || ''
      });
    }
    setValidationErrors({});
    setLogoFile(null);
    setLogoPreview(null);
    setIsEditing(false);
  };

  const handleChange = (field, value) => {
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Handle special cases
    if (field === 'billing_country') {
      // Reset state/province when country changes
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        billing_state_province: '' // Reset state/province
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handlePhoneChange = (field, value) => {
    // Auto-format phone numbers as user types
    const formattedPhone = formatPhoneForCountry(value, formData.billing_country);
    handleChange(field, formattedPhone);
  };

  const handleNotificationPreferenceChange = (key, checked) => {
    setFormData(prev => ({
      ...prev,
      notification_preferences: {
        ...prev.notification_preferences,
        [key]: checked
      }
    }));
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setValidationErrors(prev => ({ ...prev, logo_file: 'Please select an image file' }));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors(prev => ({ ...prev, logo_file: 'File size must be less than 5MB' }));
        return;
      }
      
      // Clear any existing error
      setValidationErrors(prev => ({ ...prev, logo_file: undefined }));
      
      // Set file and create preview
      setLogoFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    // Clear the file input
    const fileInput = document.getElementById('logo-file-input');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Edit Mode Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">General Settings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Company information, branding, and global preferences.
          </p>
        </div>
        
        {canEdit && !isEditing && (
          <PermissionButton
            permissions="edit_general_settings"
            onClick={() => setIsEditing(true)}
            variant="secondary"
            className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
          >
            <FiEdit2 className="mr-2 h-4 w-4" />
            Edit Settings
          </PermissionButton>
        )}

        {isEditing && (
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Edit Mode Indicator */}
      {isEditing && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FiEdit2 className="h-5 w-5 text-amber-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-800">
                <strong>Edit Mode:</strong> You are currently editing system settings. Make your changes and click "Save Changes" when finished.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Company Name *
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                validationErrors.company_name ? 'border-red-500' : 'border-gray-300'
              } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
              placeholder="3PL Logistics Inc."
              disabled={!isEditing}
              required
            />
            {validationErrors.company_name && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.company_name}</p>
            )}
            {!canEdit && (
              <p className="mt-1 text-xs text-gray-500">Read-only access</p>
            )}
          </div>

          {/* Contact Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Name</label>
            <input
              type="text"
              value={formData.contact_name}
              onChange={(e) => handleChange('contact_name', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                !isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600 border-gray-300' : 'border-gray-300'
              }`}
              placeholder="John Smith"
              disabled={!isEditing}
            />
          </div>

          {/* Billing Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Billing Email *
            </label>
            <input
              type="email"
              value={formData.billing_email}
              onChange={(e) => handleChange('billing_email', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                validationErrors.billing_email ? 'border-red-500' : 'border-gray-300'
              } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
              placeholder="billing@3pllogistics.com"
              disabled={!isEditing}
              required
            />
            {validationErrors.billing_email && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.billing_email}</p>
            )}
          </div>

          {/* Contact Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Email</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                validationErrors.contact_email ? 'border-red-500' : 'border-gray-300'
              } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
              placeholder="contact@3pllogistics.com"
              disabled={!isEditing}
            />
            {validationErrors.contact_email && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.contact_email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Support Notification Email</label>
            <input
              type="email"
              value={formData.support_email}
              onChange={(e) => handleChange('support_email', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                validationErrors.support_email ? 'border-red-500' : 'border-gray-300'
              } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
              placeholder="ops@3pllogistics.com"
              disabled={!isEditing}
            />
            <p className="mt-1 text-xs text-gray-500">
              Default internal fallback for system notification emails.
            </p>
            {validationErrors.support_email && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.support_email}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Additional Internal Notification Emails</label>
            <textarea
              rows={3}
              value={formData.support_notification_emails_text}
              onChange={(e) => handleChange('support_notification_emails_text', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                validationErrors.support_notification_emails_text ? 'border-red-500' : 'border-gray-300'
              } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
              placeholder={'ops@3pllogistics.com\nmanager@3pllogistics.com'}
              disabled={!isEditing}
            />
            <p className="mt-1 text-xs text-gray-500">
              One email per line, or separate with commas.
            </p>
            {validationErrors.support_notification_emails_text && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.support_notification_emails_text}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Internal Email Notification Preferences</label>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                ['request_created', 'New Requests'],
                ['invoice_created', 'New Invoices'],
                ['manifest_updates', 'Manifest Updates'],
                ['crossdock_updates', 'Cross-dock Updates'],
                ['low_stock', 'Low Stock Alerts'],
                ['receiving_updates', 'Receiving Updates'],
                ['shipment_updates', 'Shipment Updates']
              ].map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!formData.notification_preferences[key]}
                    onChange={(e) => handleNotificationPreferenceChange(key, e.target.checked)}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Billing Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Billing Phone</label>
            <input
              type="tel"
              value={formData.billing_phone}
              onChange={(e) => handlePhoneChange('billing_phone', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                validationErrors.billing_phone ? 'border-red-500' : 'border-gray-300'
              } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
              placeholder={getPhoneFormatExample(formData.billing_country)}
              disabled={!isEditing}
            />
            {validationErrors.billing_phone && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.billing_phone}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Format: {getPhoneFormatExample(formData.billing_country)}
            </p>
          </div>

          {/* Contact Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => handlePhoneChange('contact_phone', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                validationErrors.contact_phone ? 'border-red-500' : 'border-gray-300'
              } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
              placeholder={getPhoneFormatExample(formData.billing_country)}
              disabled={!isEditing}
            />
            {validationErrors.contact_phone && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.contact_phone}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Format: {getPhoneFormatExample(formData.billing_country)}
            </p>
          </div>

          {/* Default Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Default Timezone</label>
            <select
              value={formData.default_timezone}
              onChange={(e) => handleChange('default_timezone', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                !isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600 border-gray-300' : 'border-gray-300'
              }`}
              disabled={!isEditing}
            >
              {timezones?.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Date Format</label>
            <select
              value={formData.default_date_format}
              onChange={(e) => handleChange('default_date_format', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                !isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600 border-gray-300' : 'border-gray-300'
              }`}
              disabled={!isEditing}
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>

          {/* Time Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Time Format</label>
            <select
              value={formData.default_time_format}
              onChange={(e) => handleChange('default_time_format', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                !isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600 border-gray-300' : 'border-gray-300'
              }`}
              disabled={!isEditing}
            >
              <option value="12h">12 Hour (AM/PM)</option>
              <option value="24h">24 Hour</option>
            </select>
          </div>

          {/* Default Internal Warehouse */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Default Internal Warehouse</label>
            <select
              value={formData.default_internal_wh}
              onChange={(e) => handleChange('default_internal_wh', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                !isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600 border-gray-300' : 'border-gray-300'
              }`}
              disabled={!isEditing}
            >
              <option value="">Select a warehouse...</option>
              {warehouses?.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name} ({warehouse.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Billing Address Fields - Full Width Section */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900">Billing Address</h4>
          
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
            {/* Address Line 1 */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Address Line 1 *
              </label>
              <input
                type="text"
                value={formData.billing_address_line1}
                onChange={(e) => handleChange('billing_address_line1', e.target.value)}
                className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                  validationErrors.billing_address_line1 ? 'border-red-500' : 'border-gray-300'
                } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
                placeholder="123 Business Street"
                disabled={!isEditing}
                required
              />
              {validationErrors.billing_address_line1 && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.billing_address_line1}</p>
              )}
            </div>

            {/* Address Line 2 */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
              <input
                type="text"
                value={formData.billing_address_line2}
                onChange={(e) => handleChange('billing_address_line2', e.target.value)}
                className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                  !isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600 border-gray-300' : 'border-gray-300'
                }`}
                placeholder="Suite 100 (optional)"
                disabled={!isEditing}
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                City *
              </label>
              <input
                type="text"
                value={formData.billing_city}
                onChange={(e) => handleChange('billing_city', e.target.value)}
                className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                  validationErrors.billing_city ? 'border-red-500' : 'border-gray-300'
                } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
                placeholder="Edmonton"
                disabled={!isEditing}
                required
              />
              {validationErrors.billing_city && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.billing_city}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Country *
              </label>
              <select
                value={formData.billing_country}
                onChange={(e) => handleChange('billing_country', e.target.value)}
                className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                  validationErrors.billing_country ? 'border-red-500' : 'border-gray-300'
                } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
                disabled={!isEditing}
                required
              >
                {COUNTRIES.map((country) => (
                  <option 
                    key={country.code} 
                    value={country.code}
                    disabled={country.disabled}
                    style={country.disabled ? { fontWeight: 'bold', color: '#666' } : {}}
                  >
                    {country.name}
                  </option>
                ))}
              </select>
              {validationErrors.billing_country && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.billing_country}</p>
              )}
            </div>

            {/* State/Province - Dynamic based on country */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {stateProvinceLabel} *
              </label>
              {availableStatesProvinces.length > 0 ? (
                <select
                  value={formData.billing_state_province}
                  onChange={(e) => handleChange('billing_state_province', e.target.value)}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    validationErrors.billing_state_province ? 'border-red-500' : 'border-gray-300'
                  } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
                  disabled={!isEditing}
                  required
                >
                  <option value="">Select a {stateProvinceLabel.toLowerCase()}...</option>
                  {availableStatesProvinces.map((stateProvince) => (
                    <option key={stateProvince.code} value={stateProvince.name}>
                      {stateProvince.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={formData.billing_state_province}
                  onChange={(e) => handleChange('billing_state_province', e.target.value)}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    validationErrors.billing_state_province ? 'border-red-500' : 'border-gray-300'
                  } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
                  placeholder={stateProvinceLabel}
                  disabled={!isEditing}
                  required
                />
              )}
              {validationErrors.billing_state_province && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.billing_state_province}</p>
              )}
            </div>

            {/* Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Postal Code *
              </label>
              <input
                type="text"
                value={formData.billing_postal_code}
                onChange={(e) => handleChange('billing_postal_code', e.target.value)}
                className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                  validationErrors.billing_postal_code ? 'border-red-500' : 'border-gray-300'
                } ${!isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600' : ''}`}
                placeholder="T5J 3S2"
                disabled={!isEditing}
                required
              />
              {validationErrors.billing_postal_code && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.billing_postal_code}</p>
              )}
            </div>
          </div>
        </div>

        {/* Logo Management - Full Width */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900">Company Logo</h4>
          
          {/* Current Logo Display */}
          {(settings?.logo_url || settings?.has_logo_file || logoPreview) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Logo</label>
              <div className="flex items-center space-x-4">
                <img 
                  src={
                    logoPreview || // New uploaded preview
                    (settings?.has_logo_file ? `/api/system-settings/logo?t=${settings?.updated_at ? new Date(settings.updated_at).getTime() : Date.now()}` : settings?.logo_url) // Existing file or URL with cache busting
                  }
                  alt="Company Logo"
                  className="h-16 w-auto max-w-48 object-contain border border-gray-200 rounded bg-white p-2"
                />
                {logoPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="text-red-600 hover:text-red-800 text-sm"
                    disabled={!isEditing}
                  >
                    Remove uploaded logo
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Logo URL Option */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Logo URL</label>
            <input
              type="url"
              value={formData.logo_url}
              onChange={(e) => handleChange('logo_url', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                !isEditing ? 'bg-gray-50 cursor-not-allowed text-gray-600 border-gray-300' : 'border-gray-300'
              }`}
              placeholder="https://example.com/logo.png"
              disabled={!isEditing}
            />
            <p className="mt-1 text-xs text-gray-500">
              URL to your company logo (alternative to file upload)
            </p>
          </div>
          
          {/* OR Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">OR</span>
            </div>
          </div>
          
          {/* Logo File Upload Option */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Upload Logo File</label>
            <div className="mt-1">
              <input
                id="logo-file-input"
                type="file"
                accept="image/*"
                onChange={handleLogoFileChange}
                className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 ${
                  !isEditing ? 'cursor-not-allowed opacity-50' : ''
                }`}
                disabled={!isEditing}
              />
              {/* Debug button - temporary */}
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    const fileInput = document.getElementById('logo-file-input');
                    fileInput.click();
                  }}
                  className="mt-2 px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded border"
                >
                  DEBUG: Trigger File Input
                </button>
              )}
              {validationErrors.logo_file && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.logo_file}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Upload an image file (PNG, JPG, GIF, etc.). Max size: 5MB. File upload takes precedence over URL.
              </p>
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="flex justify-end">
            <PermissionButton
              permissions="edit_general_settings"
              type="submit"
              disabled={isSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
            >
              <FiSave className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </PermissionButton>
          </div>
        )}
      </form>
    </div>
  );
};

// Cross-Dock Settings Form Component
const CrossDockSettingsForm = () => {
  const { showSuccess, showError } = useNotification();
  const [maxDays, setMaxDays] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await adminAPI.getCrossDockSettings();
        setMaxDays(data.max_crossdock_business_days || '');
      } catch (error) {
        // Settings may not exist yet
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      await adminAPI.updateCrossDockSettings({
        max_crossdock_business_days: maxDays ? parseInt(maxDays) : null
      });
      showSuccess('Cross-dock settings saved');
    } catch (error) {
      showError('Failed to save cross-dock settings');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-1">Cross-Dock Settings</h4>
        <p className="text-sm text-gray-500">
          Configure rules for cross-dock requests.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <label htmlFor="max-crossdock-days" className="block text-sm font-medium text-gray-900">
          Maximum Business Days Between Arrival and Pickup
        </label>
        <p className="text-sm text-gray-500 mt-1">
          Limits how far out a customer can set their pickup date from the arrival date. Weekends are excluded from the count. Leave blank for no limit.
        </p>
        <div className="mt-3 flex items-center space-x-3">
          <input
            id="max-crossdock-days"
            type="number"
            min="1"
            max="90"
            value={maxDays}
            onChange={(e) => setMaxDays(e.target.value)}
            placeholder="No limit"
            className="block w-32 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
          <span className="text-sm text-gray-500">business days</span>
        </div>
      </div>

      <div className="pt-2">
        <button
          onClick={handleSave}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <FiSave className="mr-2 h-4 w-4" />
          Save Cross-Dock Settings
        </button>
      </div>
    </div>
  );
};

// Landing Page Branding Form Component
const LandingPageBrandingForm = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [siteTitle, setSiteTitle] = useState('');
  const [landingLogo, setLandingLogo] = useState(null);
  const [landingLogoPreview, setLandingLogoPreview] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);

  const { data: brandingData, isLoading } = useQuery(
    'landingBranding',
    () => adminAPI.getLandingBranding(),
    { staleTime: 60000 }
  );

  useEffect(() => {
    if (brandingData) {
      setSiteTitle(brandingData.site_title || '');
      if (brandingData.has_landing_logo) {
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
        setLandingLogoPreview(`${apiBase}/system-settings/landing-logo?t=${brandingData.updated_at ? new Date(brandingData.updated_at).getTime() : ''}`);
      }
      if (brandingData.has_favicon) {
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
        setFaviconPreview(`${apiBase}/system-settings/favicon?t=${brandingData.updated_at ? new Date(brandingData.updated_at).getTime() : ''}`);
      }
    }
  }, [brandingData]);

  const saveMutation = useMutation(
    (formData) => adminAPI.updateLandingBranding(formData),
    {
      onSuccess: () => {
        showSuccess('Landing page branding updated');
        queryClient.invalidateQueries('landingBranding');
        setLandingLogo(null);
        setFavicon(null);
      },
      onError: (error) => {
        showError(error.response?.data?.error || 'Failed to update branding');
      }
    }
  );

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLandingLogo(file);
      setLandingLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFavicon(file);
      setFaviconPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    const formData = new FormData();
    formData.append('site_title', siteTitle);
    if (landingLogo) formData.append('landing_logo', landingLogo);
    if (favicon) formData.append('favicon', favicon);
    saveMutation.mutate(formData);
  };

  const handleRemoveLogo = () => {
    const formData = new FormData();
    formData.append('site_title', siteTitle);
    formData.append('remove_landing_logo', 'true');
    saveMutation.mutate(formData);
    setLandingLogoPreview(null);
    setLandingLogo(null);
  };

  const handleRemoveFavicon = () => {
    const formData = new FormData();
    formData.append('site_title', siteTitle);
    formData.append('remove_favicon', 'true');
    saveMutation.mutate(formData);
    setFaviconPreview(null);
    setFavicon(null);
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg leading-6 font-medium text-gray-900">Landing Page Branding</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure the logo, favicon, and site title shown on your public landing page. These are separate from the internal admin branding.
        </p>
      </div>

      {/* Site Title */}
      <div className="bg-gray-50 rounded-lg p-6">
        <label htmlFor="site-title" className="block text-sm font-medium text-gray-900">Site Title</label>
        <p className="text-sm text-gray-500 mt-1">Shown in the browser tab. Leave blank to use "Datavisual 3PL+".</p>
        <input
          id="site-title"
          type="text"
          value={siteTitle}
          onChange={(e) => setSiteTitle(e.target.value)}
          placeholder="Datavisual 3PL+"
          className="mt-3 block w-full max-w-md border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />
      </div>

      {/* Landing Page Logo */}
      <div className="bg-gray-50 rounded-lg p-6">
        <label className="block text-sm font-medium text-gray-900">Landing Page Logo</label>
        <p className="text-sm text-gray-500 mt-1">Shown in the navigation bar and footer of the landing page. Can be different from the admin sidebar logo.</p>
        <div className="mt-4 flex items-center space-x-6">
          {landingLogoPreview && (
            <div className="flex-shrink-0">
              <img src={landingLogoPreview} alt="Landing logo preview" className="h-16 w-auto rounded border border-gray-200 bg-white p-2" />
            </div>
          )}
          <div className="flex items-center space-x-3">
            <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              {landingLogoPreview ? 'Change Logo' : 'Upload Logo'}
              <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
            </label>
            {landingLogoPreview && (
              <button onClick={handleRemoveLogo} className="text-sm text-red-600 hover:text-red-800">Remove</button>
            )}
          </div>
        </div>
      </div>

      {/* Favicon */}
      <div className="bg-gray-50 rounded-lg p-6">
        <label className="block text-sm font-medium text-gray-900">Favicon</label>
        <p className="text-sm text-gray-500 mt-1">The small icon shown in the browser tab. Recommended size: 32x32 or 64x64 pixels. PNG or ICO format.</p>
        <div className="mt-4 flex items-center space-x-6">
          {faviconPreview && (
            <div className="flex-shrink-0">
              <img src={faviconPreview} alt="Favicon preview" className="h-10 w-10 rounded border border-gray-200 bg-white p-1" />
            </div>
          )}
          <div className="flex items-center space-x-3">
            <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              {faviconPreview ? 'Change Favicon' : 'Upload Favicon'}
              <input type="file" accept="image/*,.ico" onChange={handleFaviconChange} className="hidden" />
            </label>
            {faviconPreview && (
              <button onClick={handleRemoveFavicon} className="text-sm text-red-600 hover:text-red-800">Remove</button>
            )}
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={saveMutation.isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          <FiSave className="mr-2 h-4 w-4" />
          {saveMutation.isLoading ? 'Saving...' : 'Save Landing Page Settings'}
        </button>
      </div>
    </div>
  );
};

const Settings = () => {
  const queryClient = useQueryClient();
  // Tabs removed -- settings now displayed as stacked blocks
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [viewingWarehouse, setViewingWarehouse] = useState(null);
  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, warehouse: null });
  const { showSuccess, showError } = useNotification();

  // Role management state
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [isEditingRole, setIsEditingRole] = useState(false);

  // Permission checks
  const {
    canViewGeneralSettings, canEditGeneralSettings,
    canViewNotifications, canEditNotifications, canManageEmailTemplates, canManageEscalationRules,
    canViewSecuritySettings, canEditSecuritySettings,
    canViewWarehouses, canCreateWarehouses, canEditWarehouses, canDeleteWarehouses,
    canManageRoles, canManageTeams
  } = usePermissions();

  // Determine which tabs user can access
  const hasGeneralAccess = canViewGeneralSettings || canEditGeneralSettings;
  const hasNotificationAccess = canViewNotifications || canEditNotifications;
  const hasSecurityAccess = canViewSecuritySettings || canEditSecuritySettings;
  const hasWarehouseAccess = canViewWarehouses || canCreateWarehouses || canEditWarehouses || canDeleteWarehouses;
  const hasRoleAccess = canManageRoles;
  const hasLandingAccess = canEditGeneralSettings;

  // Fetch warehouses data - always call this hook, but conditionally enable it
  const { data: warehouses, isLoading: warehousesLoading, error: warehousesError } = useQuery(
    'warehouses',
    () => adminAPI.getWarehouses(),
    { enabled: hasWarehouseAccess }
  );

  // Fetch system settings data
  const { data: systemSettings, isLoading: settingsLoading, error: settingsError } = useQuery(
    'systemSettings',
    () => adminAPI.getSystemSettings(),
    { enabled: hasGeneralAccess }
  );

  // Fetch timezones
  const { data: timezones } = useQuery(
    'timezones',
    () => adminAPI.getTimezones(),
    { enabled: hasGeneralAccess }
  );

  // Fetch roles for role management
  const { data: rolesData, isLoading: rolesLoading } = useQuery(
    'admin-roles',
    () => adminAPI.getRoles(),
    { enabled: canManageRoles }
  );

  // Fetch role permissions when a role is selected
  const { data: rolePermissionsData, isLoading: rolePermissionsLoading } = useQuery(
    ['role-permissions', selectedRole?.id],
    () => adminAPI.getRolePermissions(selectedRole.id),
    { 
      enabled: !!selectedRole && canManageRoles,
      // Remove onSuccess to prevent stale state issues
    }
  );

  // Update role permissions when data changes
  useEffect(() => {
    if (rolePermissionsData?.permissions) {
      setRolePermissions(rolePermissionsData.permissions);
    } else {
      setRolePermissions({});
    }
  }, [rolePermissionsData, selectedRole?.id]);

  // Reset role selection state when switching roles
  useEffect(() => {
    setIsEditingRole(false);
  }, [selectedRole?.id]);

  // Create warehouse mutation - updated with custom notifications
  const createWarehouse = useMutation(
    (warehouseData) => adminAPI.createWarehouse(warehouseData),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('warehouses');
        setShowWarehouseForm(false);
        showSuccess(response?.message || 'Warehouse created successfully');
      },
      onError: (error) => {
        showError(error.response?.data?.error || 'Failed to create warehouse');
      }
    }
  );

  // Update system settings mutation - updated with custom notifications
  const updateSystemSettings = useMutation(
    (settingsData) => adminAPI.updateSystemSettings(settingsData),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('systemSettings');
        queryClient.invalidateQueries('systemBranding');
        // Force refetch of system branding immediately to update logos across the app
        queryClient.refetchQueries('systemBranding');
        showSuccess(response?.message || 'System settings updated successfully');
      },
      onError: (error) => {
        showError(error.response?.data?.error || error.message || 'Failed to update system settings');
      }
    }
  );

  // Update warehouse mutation - updated with custom notifications
  const updateWarehouse = useMutation(
    ({ id, data }) => adminAPI.updateWarehouse(id, data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('warehouses');
        setEditingWarehouse(null);
        showSuccess(response?.message || 'Warehouse updated successfully');
      },
      onError: (error) => {
        showError(error.response?.data?.error || error.message || 'Failed to update warehouse');
      }
    }
  );

  // Delete warehouse mutation - updated with custom notifications
  const deleteWarehouse = useMutation(
    (id) => adminAPI.deleteWarehouse(id),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('warehouses');
        showSuccess(response?.message || 'Warehouse deleted successfully');
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.transactionCount > 0) {
          showError('Cannot delete warehouse with transactions', 'Deletion Blocked');
        } else {
          showError(errorData?.error || error.message || 'Failed to delete warehouse');
        }
      }
    }
  );

  // Update role permissions mutation
  const updateRolePermissionsMutation = useMutation(
    ({ roleId, permissions }) => adminAPI.updateRolePermissions(roleId, permissions),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['role-permissions', selectedRole?.id]);
        queryClient.invalidateQueries('admin-roles');
        setIsEditingRole(false);
        showSuccess(response?.message || 'Role permissions updated successfully');
      },
      onError: (error) => {
        showError(error.response?.data?.message || error.message || 'Failed to update role permissions');
      }
    }
  );

  // Tabs removed -- all sections visible as stacked blocks

  // If no settings access at all, show access denied - move this after all hooks
  if (!hasGeneralAccess && !hasNotificationAccess && !hasSecurityAccess && !hasWarehouseAccess && !hasRoleAccess) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 text-center">
            <FiLock className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">
              You don't have permission to access system settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Add the inline warehouse form component
  const WarehouseFormInline = ({ warehouse, onSubmit, onCancel, onDelete, isLoading }) => {
    const [formData, setFormData] = useState({
      name: warehouse?.name || '',
      code: warehouse?.code || '',
      address_line1: warehouse?.address_line1 || '',
      address_line2: warehouse?.address_line2 || '',
      city: warehouse?.city || '',
      state_province: warehouse?.state_province || '',
      postal_code: warehouse?.postal_code || '',
      country: warehouse?.country || 'CA',
      default_zone: warehouse?.default_zone || '',
      is_active: warehouse?.is_active !== undefined ? warehouse.is_active : true
    });

    const [validationErrors, setValidationErrors] = useState({});

    // Get available states/provinces based on selected country
    const availableStatesProvinces = getStatesProvinces(formData.country);
    const stateProvinceLabel = getStateProvinceLabel(formData.country);

    const validateForm = () => {
      const errors = {};
      if (!formData.name) errors.name = 'Warehouse name is required';
      if (!formData.code) errors.code = 'Warehouse code is required';
      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!validateForm()) return;
      onSubmit(formData);
    };

    const handleChange = (field, value) => {
      if (validationErrors[field]) {
        setValidationErrors(prev => ({ ...prev, [field]: undefined }));
      }

      if (field === 'country') {
        setFormData(prev => ({ ...prev, [field]: value, state_province: '' }));
      } else if (field === 'code') {
        setFormData(prev => ({ ...prev, [field]: value.toUpperCase() }));
      } else {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h5 className="text-md font-medium text-gray-900">Basic Information</h5>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Warehouse Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                  validationErrors.name ? 'border-red-500' : ''
                }`}
                placeholder="Main Warehouse"
                required
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Warehouse Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value)}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                  validationErrors.code ? 'border-red-500' : ''
                }`}
                placeholder="MAIN"
                maxLength="20"
                required
              />
              {validationErrors.code && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.code}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Default Zone</label>
              <input
                type="text"
                value={formData.default_zone}
                onChange={(e) => handleChange('default_zone', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="A1"
                maxLength="50"
              />
            </div>

            <div className="flex items-center pt-6">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">Active warehouse</label>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="space-y-4">
          <h5 className="text-md font-medium text-gray-900">Address</h5>
          
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
              <input
                type="text"
                value={formData.address_line1}
                onChange={(e) => handleChange('address_line1', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="123 Industrial Blvd"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
              <input
                type="text"
                value={formData.address_line2}
                onChange={(e) => handleChange('address_line2', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Unit 100 (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Edmonton"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <select
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                {COUNTRIES.map((country) => (
                  <option 
                    key={country.code} 
                    value={country.code}
                    disabled={country.disabled}
                    style={country.disabled ? { fontWeight: 'bold', color: '#666' } : {}}
                  >
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{stateProvinceLabel}</label>
              {availableStatesProvinces.length > 0 ? (
                <select
                  value={formData.state_province}
                  onChange={(e) => handleChange('state_province', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select a {stateProvinceLabel.toLowerCase()}...</option>
                  {availableStatesProvinces.map((stateProvince) => (
                    <option key={stateProvince.code} value={stateProvince.name}>
                      {stateProvince.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={formData.state_province}
                  onChange={(e) => handleChange('state_province', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={stateProvinceLabel}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Postal Code</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="T5J 3S2"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          
          {/* Delete button - only show when editing existing warehouse */}
          {warehouse && (
            <PermissionButton
              permissions="delete_warehouses"
              onClick={() => onDelete(warehouse)}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 border-transparent"
            >
              <FiTrash2 className="mr-2 h-4 w-4" />
              Delete Warehouse
            </PermissionButton>
          )}
          
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <FiSave className="mr-2 h-4 w-4 inline" />
            {isLoading ? 'Saving...' : (warehouse ? 'Update Warehouse' : 'Create Warehouse')}
          </button>
        </div>
      </form>
    );
  };

  // Warehouse View Component
  const WarehouseView = ({ warehouse }) => {
    const availableStatesProvinces = getStatesProvinces(warehouse.country || 'CA');
    const stateProvinceLabel = getStateProvinceLabel(warehouse.country || 'CA');

    return (
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h5 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
            Basic Information
          </h5>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warehouse Name
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                {warehouse.name || '-'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warehouse Code
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {warehouse.code || '-'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Zone
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                {warehouse.default_zone || '-'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <div className="bg-gray-50 p-3 rounded-md border">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  warehouse.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {warehouse.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="space-y-4">
          <h5 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
            Address Information
          </h5>
          
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 1
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                {warehouse.address_line1 || '-'}
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 2
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                {warehouse.address_line2 || '-'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                {warehouse.city || '-'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                {warehouse.country ? 
                  COUNTRIES.find(c => c.code === warehouse.country)?.name || warehouse.country 
                  : '-'
                }
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {stateProvinceLabel}
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                {warehouse.state_province || '-'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postal Code
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                {warehouse.postal_code || '-'}
              </div>
            </div>
          </div>
        </div>

        {/* Full Address Display */}
        {(warehouse.address_line1 || warehouse.city) && (
          <div className="space-y-4">
            <h5 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
              Full Address
            </h5>
            <div className="bg-gray-50 p-4 rounded-md border">
              <div className="flex items-start">
                <FiMapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div className="text-sm text-gray-900 space-y-1">
                  {warehouse.address_line1 && <div>{warehouse.address_line1}</div>}
                  {warehouse.address_line2 && <div>{warehouse.address_line2}</div>}
                  {(warehouse.city || warehouse.state_province) && (
                    <div>
                      {warehouse.city && warehouse.state_province ? 
                        `${warehouse.city}, ${warehouse.state_province}` : 
                        warehouse.city || warehouse.state_province
                      }
                    </div>
                  )}
                  {warehouse.postal_code && <div>{warehouse.postal_code}</div>}
                  {warehouse.country && (
                    <div>{COUNTRIES.find(c => c.code === warehouse.country)?.name || warehouse.country}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Role Management Component
  const RoleManagement = () => {
    const roles = rolesData?.data || [];
    
    // Separate internal and customer roles
    const customerRoles = roles.filter(role => 
      ['customer_admin', 'regular_user'].includes(role.name)
    );
    
    const internalRoles = roles.filter(role => 
      !['customer_admin', 'regular_user', 'system_admin'].includes(role.name)
    );
    
    // Group permissions by category for better organization
    const permissionCategories = {
      'Core Operations': ['dashboard', 'view_inventory', 'manage_inventory', 'incoming_inventory', 'pick_pack', 'ship_products', 'kit_building'],
      'Customer Management': ['view_customers', 'manage_customers', 'create_customers', 'edit_customers', 'delete_customers'],
      'Requests & Orders': ['view_requests', 'manage_requests', 'create_requests', 'edit_requests', 'delete_requests'],
      'Invoicing & Billing': ['invoices', 'view_invoices', 'manage_invoices', 'create_invoices', 'edit_invoices', 'delete_invoices'],
      'User Management': ['view_users', 'manage_users', 'create_users', 'edit_users', 'delete_users'],
      'System Settings': ['settings', 'view_settings', 'manage_settings', 'view_general_settings', 'edit_general_settings', 'create_settings', 'edit_settings', 'delete_settings'],
      'Security & Access': ['view_security_settings', 'edit_security_settings', 'manage_roles', 'manage_teams'],
      'Notifications': ['view_notifications', 'edit_notifications', 'manage_email_templates', 'manage_escalation_rules'],
      'Warehouses': ['view_warehouses', 'create_warehouses', 'edit_warehouses', 'delete_warehouses']
    };

    const handleRoleSelect = (role) => {
      if (isEditingRole) {
        // Warn user about unsaved changes
        if (window.confirm('You have unsaved changes. Are you sure you want to switch roles?')) {
          setSelectedRole(role);
          setIsEditingRole(false);
        }
      } else {
        setSelectedRole(role);
      }
    };

    const handlePermissionChange = (permissionName, enabled) => {
      setRolePermissions(prev => ({
        ...prev,
        [permissionName]: {
          ...prev[permissionName],
          enabled
        }
      }));
    };

    const handleSavePermissions = () => {
      if (!selectedRole) return;
      
      // Convert permission object to simple boolean mapping
      const permissionsBooleanMap = {};
      Object.keys(rolePermissions).forEach(permissionName => {
        permissionsBooleanMap[permissionName] = rolePermissions[permissionName]?.enabled || false;
      });
      
      updateRolePermissionsMutation.mutate({
        roleId: selectedRole.id,
        permissions: permissionsBooleanMap
      });
    };

    const handleCancelEdit = () => {
      setIsEditingRole(false);
      // Reset to original permissions
      if (rolePermissionsData?.permissions) {
        setRolePermissions(rolePermissionsData.permissions);
      }
    };

    // Check if role is protected (cannot be edited)
    const isProtectedRole = (roleName) => {
      return ['system_admin'].includes(roleName);
    };

    // Check if role is a customer template (should be carefully managed)
    const isCustomerTemplate = (roleName) => {
      return ['customer_admin', 'regular_user'].includes(roleName);
    };

    const RoleSection = ({ title, rolesList, description, warningMessage }) => (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-4">
          <h4 className="text-md font-medium text-gray-900">{title}</h4>
          {description && (
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          )}
          {warningMessage && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FiAlertTriangle className="h-4 w-4 text-amber-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs text-amber-800">{warningMessage}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {rolesList.length === 0 ? (
          <p className="text-gray-500 text-sm italic">No roles in this category</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rolesList.map((role) => (
              <button
                key={role.id}
                onClick={() => handleRoleSelect(role)}
                className={`text-left p-4 rounded-lg border-2 transition-colors ${
                  selectedRole?.id === role.id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h5 className="text-sm font-medium text-gray-900">
                    {role.name.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </h5>
                  <div className="flex space-x-1">
                    {isProtectedRole(role.name) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        Protected
                      </span>
                    )}
                    {isCustomerTemplate(role.name) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Customer
                      </span>
                    )}
                  </div>
                </div>
                {role.description && (
                  <p className="mt-1 text-xs text-gray-500">{role.description}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Role Management</h3>
            <p className="mt-1 text-sm text-gray-500">
              Manage internal staff roles and customer role templates. Select a role to view and edit its permissions.
            </p>
          </div>
          
          {selectedRole && !isEditingRole && !isProtectedRole(selectedRole.name) && (
            <button
              onClick={() => setIsEditingRole(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FiEdit2 className="mr-2 h-4 w-4 inline" />
              Edit Permissions
            </button>
          )}
        </div>

        {/* Role Selection Sections */}
        <div className="space-y-6">
          <RoleSection 
            title="Internal Staff Roles"
            rolesList={internalRoles}
            description="Roles for internal staff members (admin, manager, warehouse, accounting, etc.)"
          />
          
          <RoleSection 
            title="Customer Role Templates (DEFAULT)"
            rolesList={customerRoles}
            description="Default permission templates for new customer users. Customers can grant additional permissions to individual users as needed."
            warningMessage="These are default templates only. Customer admins can grant additional permissions to their users beyond these defaults."
          />
        </div>

        {/* Permission Matrix */}
        {selectedRole && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h4 className="text-md font-medium text-gray-900">
                  Permissions for {selectedRole.name.split('_').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </h4>
                {isCustomerTemplate(selectedRole.name) && (
                  <p className="text-sm text-blue-600 mt-1">
                    Customer DEFAULT Role Template - These are baseline permissions. Customer admins can grant additional permissions to individual users.
                  </p>
                )}
              </div>
              
              {isEditingRole && !updateRolePermissionsMutation.isLoading && (
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePermissions}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FiSave className="mr-2 h-4 w-4 inline" />
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            {isProtectedRole(selectedRole.name) && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FiShield className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      <strong>Protected Role:</strong> This role's permissions cannot be modified as it's a system-protected role.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isEditingRole && !isProtectedRole(selectedRole.name) && (
              <div className={`border rounded-md p-4 mb-6 ${
                isCustomerTemplate(selectedRole.name) 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FiEdit2 className={`h-5 w-5 ${
                      isCustomerTemplate(selectedRole.name) ? 'text-blue-400' : 'text-amber-400'
                    }`} />
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm ${
                      isCustomerTemplate(selectedRole.name) ? 'text-blue-800' : 'text-amber-800'
                    }`}>
                      <strong>Edit Mode:</strong> You are currently editing role permissions. 
                      {isCustomerTemplate(selectedRole.name) && (
                        <span className="block mt-1">
                          <strong>Customer Template Note:</strong> These are DEFAULT permissions. Customer admins can grant additional permissions to their users beyond these defaults.
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {rolePermissionsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="space-y-6">
                {Object.entries(permissionCategories).map(([category, permissionNames]) => {
                  // Filter permissions that exist in this role
                  const categoryPermissions = permissionNames.filter(permName => 
                    rolePermissions[permName]
                  );
                  
                  if (categoryPermissions.length === 0) return null;

                  return (
                    <div key={category} className="border border-gray-200 rounded-lg">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <h5 className="text-sm font-medium text-gray-900">{category}</h5>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {categoryPermissions.map(permissionName => {
                            const permission = rolePermissions[permissionName];
                            if (!permission) return null;

                            return (
                              <div key={permissionName} className="flex items-start">
                                <div className="flex items-center h-5">
                                  <input
                                    id={`permission-${permissionName}`}
                                    type="checkbox"
                                    checked={permission.enabled || false}
                                    onChange={(e) => handlePermissionChange(permissionName, e.target.checked)}
                                    disabled={!isEditingRole || isProtectedRole(selectedRole.name)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                                  />
                                </div>
                                <div className="ml-3">
                                  <label 
                                    htmlFor={`permission-${permissionName}`} 
                                    className={`text-sm font-medium ${
                                      !isEditingRole || isProtectedRole(selectedRole.name) 
                                        ? 'text-gray-600' 
                                        : 'text-gray-900 cursor-pointer'
                                    }`}
                                  >
                                    {permission.name.split('_').map(word => 
                                      word.charAt(0).toUpperCase() + word.slice(1)
                                    ).join(' ')}
                                  </label>
                                  {permission.description && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {permission.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {updateRolePermissionsMutation.isLoading && (
              <div className="mt-6 flex items-center justify-center">
                <LoadingSpinner />
                <span className="ml-2 text-sm text-gray-600">Updating permissions...</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">System Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage system configuration, warehouses, and user permissions
        </p>
      </div>

      <div className="space-y-8">

        {/* General Settings */}
        {hasGeneralAccess && (
          <SettingsBlock title="General Settings" subtitle="Company information, billing address, and system defaults" icon={<FiSettings className="h-5 w-5" />}>
            {settingsLoading ? (
              <LoadingSpinner />
            ) : settingsError ? (
              <ErrorAlert message="Failed to load system settings" />
            ) : (
              <GeneralSettingsForm
                settings={systemSettings}
                warehouses={warehouses}
                timezones={timezones}
                canEdit={canEditGeneralSettings}
                onSave={(data) => updateSystemSettings.mutate(data)}
                isSaving={updateSystemSettings.isLoading}
              />
            )}
          </SettingsBlock>
        )}

          {/* Notifications Tab */}
        {/* Notifications */}
        {hasNotificationAccess && (
          <SettingsBlock title="Notifications" subtitle="System alerts, email templates, and escalation rules" icon={<FiBell className="h-5 w-5" />}>
            <div className="space-y-6">

              <div className="space-y-6">
                {/* System Alerts */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">System Alerts</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Low Stock Alerts</label>
                        <p className="text-xs text-gray-500">Notify when inventory falls below reorder threshold</p>
                      </div>
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        disabled={!canEditNotifications}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Stuck Pick Alerts</label>
                        <p className="text-xs text-gray-500">Alert when picks remain unprocessed for 24 hours</p>
                      </div>
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        disabled={!canEditNotifications}
                      />
                    </div>
                  </div>
                </div>

                {/* Email Templates - Manager/Admin Only */}
                <PermissionGate permissions="manage_email_templates">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Email Templates</h4>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="text-sm text-gray-600">Configure email templates for packing slips, ASN notices, and other automated communications.</p>
                      <button className="mt-2 text-sm text-indigo-600 hover:text-indigo-500">
                        Manage Templates
                      </button>
                    </div>
                  </div>
                </PermissionGate>

                {canEditNotifications && (
                  <div className="flex justify-end">
                    <PermissionButton
                      permissions="edit_notifications"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
                    >
                      <FiSave className="mr-2 h-4 w-4" />
                      Save Changes
                    </PermissionButton>
                  </div>
                )}
              </div>
            </div>
          </SettingsBlock>
        )}

        {/* Security */}
        {hasSecurityAccess && (
          <SettingsBlock title="Security" subtitle="Password policies, session management, and access controls" icon={<FiShield className="h-5 w-5" />}>
            <PermissionGate 
              permissions="view_security_settings"
              fallback={
                <div className="text-center py-12">
                  <FiLock className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    You don't have permission to access security settings.
                  </p>
                </div>
              }
            >
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Security Settings</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Configure security policies, authentication, and access controls.
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Password Policy</h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Minimum Length</label>
                        <input
                          type="number"
                          min="6"
                          max="32"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          defaultValue="8"
                          disabled={!canEditSecuritySettings}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Password Expiry (days)</label>
                        <input
                          type="number"
                          min="0"
                          max="365"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          defaultValue="90"
                          disabled={!canEditSecuritySettings}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Session Management</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Session Timeout (minutes)</label>
                      <input
                        type="number"
                        min="15"
                        max="480"
                        className="mt-1 block w-24 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        defaultValue="30"
                        disabled={!canEditSecuritySettings}
                      />
                    </div>
                  </div>

                  {canEditSecuritySettings && (
                    <div className="flex justify-end">
                      <PermissionButton
                        permissions="edit_security_settings"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
                      >
                        <FiSave className="mr-2 h-4 w-4" />
                        Save Changes
                      </PermissionButton>
                    </div>
                  )}
                </div>
              </div>
            </PermissionGate>
          </SettingsBlock>
        )}

        {/* Warehouses */}
        {hasWarehouseAccess && (
          <SettingsBlock title="Warehouses" subtitle="Manage warehouse locations and configurations" icon={<FiBox className="h-5 w-5" />} defaultOpen={false}>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Warehouse Management</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Manage internal warehouse locations, zones, and settings.
                  </p>
                </div>
                {!showWarehouseForm && !editingWarehouse && !viewingWarehouse && (
                  <PermissionButton
                    permissions="create_warehouses"
                    onClick={() => setShowWarehouseForm(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
                  >
                    <FiPlus className="mr-2 h-4 w-4" />
                    Add Warehouse
                  </PermissionButton>
                )}
              </div>

              {/* Warehouse View */}
              {viewingWarehouse && (
                <div className="bg-white rounded-lg p-6 border">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-lg font-medium text-gray-900">
                      Warehouse Details
                    </h4>
                    <div className="flex space-x-3">
                      <PermissionButton
                        permissions="edit_warehouses"
                        onClick={() => {
                          setEditingWarehouse(viewingWarehouse);
                          setViewingWarehouse(null);
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
                      >
                        <FiEdit2 className="mr-2 h-4 w-4" />
                        Edit Warehouse
                      </PermissionButton>
                      <button
                        onClick={() => setViewingWarehouse(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <FiX className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <WarehouseView warehouse={viewingWarehouse} />
                </div>
              )}

              {/* Inline Warehouse Form */}
              {(showWarehouseForm || editingWarehouse) && (
                <div className="bg-gray-50 rounded-lg p-6 border">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-lg font-medium text-gray-900">
                      {editingWarehouse ? 'Edit Warehouse' : 'Create New Warehouse'}
                    </h4>
                    <button
                      onClick={() => {
                        setShowWarehouseForm(false);
                        setEditingWarehouse(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <FiX className="h-5 w-5" />
                    </button>
                  </div>
                  <WarehouseFormInline 
                    warehouse={editingWarehouse}
                    onSubmit={(data) => {
                      if (editingWarehouse) {
                        updateWarehouse.mutate({ id: editingWarehouse.id, data });
                      } else {
                        createWarehouse.mutate(data);
                      }
                    }}
                    onCancel={() => {
                      setShowWarehouseForm(false);
                      setEditingWarehouse(null);
                    }}
                    onDelete={(warehouse) => setDeleteConfirm({ isOpen: true, warehouse })}
                    isLoading={createWarehouse.isLoading || updateWarehouse.isLoading}
                  />
                </div>
              )}

              {warehousesLoading ? (
                <LoadingSpinner />
              ) : warehousesError ? (
                <ErrorAlert message="Failed to load warehouses" />
              ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                  {/* Desktop Table View */}
                  <div className="hidden lg:block">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Warehouse
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Code
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Location
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Default Zone
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {warehouses?.length > 0 ? (
                          warehouses.map((warehouse) => (
                            <tr key={warehouse.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <FiBox className="h-5 w-5 text-gray-400 mr-3" />
                                  <div className="text-sm font-medium text-gray-900">
                                    {warehouse.name}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {warehouse.code}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {warehouse.city && warehouse.state_province ? 
                                    `${warehouse.city}, ${warehouse.state_province}` : 
                                    warehouse.city || warehouse.state_province || '-'
                                  }
                                </div>
                                {warehouse.country && (
                                  <div className="text-xs text-gray-500">{warehouse.country}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {warehouse.default_zone || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  warehouse.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {warehouse.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-3">
                                  <PermissionButton
                                    permissions="view_warehouses"
                                    onClick={() => setViewingWarehouse(warehouse)}
                                    className="text-indigo-600 hover:text-indigo-900 p-0 border-0 bg-transparent shadow-none focus:ring-0"
                                  >
                                    <FiEye className="h-5 w-5" />
                                    <span className="sr-only">View</span>
                                  </PermissionButton>
                                  
                                  <PermissionButton
                                    permissions="edit_warehouses"
                                    onClick={() => setEditingWarehouse(warehouse)}
                                    className="text-blue-600 hover:text-blue-900 p-0 border-0 bg-transparent shadow-none focus:ring-0"
                                  >
                                    <FiEdit className="h-5 w-5" />
                                    <span className="sr-only">Edit</span>
                                  </PermissionButton>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                              <FiBox className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                              <p className="font-medium">No warehouses</p>
                              <p>Get started by creating your first warehouse.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile View */}
                  <div className="block lg:hidden">
                    {warehouses?.length > 0 ? (
                      warehouses.map((warehouse) => (
                        <div key={warehouse.id} className="mb-4">
                          <div className="flex items-center">
                            <FiBox className="h-5 w-5 text-gray-400 mr-3" />
                            <div className="text-sm font-medium text-gray-900">
                              {warehouse.name}
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            <div>
                              <strong>Code:</strong> {warehouse.code}
                            </div>
                            <div>
                              <strong>Location:</strong> {warehouse.city && warehouse.state_province ? `${warehouse.city}, ${warehouse.state_province}` : warehouse.city || warehouse.state_province || '-'}
                            </div>
                            <div>
                              <strong>Default Zone:</strong> {warehouse.default_zone || '-'}
                            </div>
                            <div>
                              <strong>Status:</strong> {warehouse.is_active ? 'Active' : 'Inactive'}
                            </div>
                          </div>
                          <div className="flex justify-end space-x-3 pt-4 border-t">
                            <PermissionButton
                              permissions="view_warehouses"
                              onClick={() => setViewingWarehouse(warehouse)}
                              className="text-indigo-600 hover:text-indigo-900 p-0 border-0 bg-transparent shadow-none focus:ring-0"
                            >
                              <FiEye className="h-5 w-5" />
                              <span className="sr-only">View</span>
                            </PermissionButton>
                            
                            <PermissionButton
                              permissions="edit_warehouses"
                              onClick={() => setEditingWarehouse(warehouse)}
                              className="text-blue-600 hover:text-blue-900 p-0 border-0 bg-transparent shadow-none focus:ring-0"
                            >
                              <FiEdit className="h-5 w-5" />
                              <span className="sr-only">Edit</span>
                            </PermissionButton>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-sm text-gray-500">
                        <FiBox className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                        <p className="font-medium">No warehouses</p>
                        <p>Get started by creating your first warehouse.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </SettingsBlock>
        )}

        {/* Roles & Permissions */}
        {hasRoleAccess && (
          <SettingsBlock title="Roles & Permissions" subtitle="Manage user roles and access permissions" icon={<FiUsers className="h-5 w-5" />} defaultOpen={false}>
            <PermissionGate 
              permissions="manage_roles"
              fallback={
                <div className="text-center py-12">
                  <FiShield className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    You don't have permission to manage roles and permissions.
                  </p>
                </div>
              }
            >
              <RoleManagement />
            </PermissionGate>
          </SettingsBlock>
        )}

        {/* Inbound Request Settings */}
        {hasGeneralAccess && (
          <SettingsBlock title="Inbound Request Settings" subtitle="Cross-dock configuration and inbound processing rules" icon={<FiTruck className="h-5 w-5" />} defaultOpen={false}>
            <CrossDockSettingsForm />
          </SettingsBlock>
        )}

        {/* Landing Page */}
        {hasLandingAccess && (
          <SettingsBlock title="Landing Page" subtitle="Public website branding, logo, and favicon" icon={<FiEye className="h-5 w-5" />} defaultOpen={false}>
            <LandingPageBrandingForm />
          </SettingsBlock>
        )}

        {/* Changelog */}
        {hasGeneralAccess && (
          <SettingsBlock title="Changelog" subtitle="Manage release notes shown in the customer and admin changelog pages" icon={<FiEdit2 className="h-5 w-5" />} defaultOpen={false}>
            <ChangelogSettingsForm canEdit={canEditGeneralSettings} />
          </SettingsBlock>
        )}

      </div>

      {/* Confirm Dialog for Warehouse Deletion */}
      {deleteConfirm.isOpen && (
        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, warehouse: null })}
          onConfirm={() => {
            deleteWarehouse.mutate(deleteConfirm.warehouse.id);
            setDeleteConfirm({ isOpen: false, warehouse: null });
          }}
          title="Confirm Warehouse Deletion"
          message={`Are you sure you want to delete "${deleteConfirm.warehouse?.name}"? This action cannot be undone and will only proceed if the warehouse has no transactions.`}
          confirmText="Delete"
          type="danger"
        />
      )}
    </div>
  );
};

export default Settings; 
